import { appointmentStatusSemantic } from '@scripsteam/scrips-signal-ds';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { CancellationSummary } from '../cancellation-summary';

/**
 * AppointmentStatusSummary — the status line at the TOP of the Edit-Appointment form (19 Oct, Antonin).
 *
 * ONE line: the label "Status", the actual status as the app's soft status chip (BridgeStatusChip, semantic
 * from the DS's single appointment-status map, the same one the Practitioner app reads; Fulfilled reads
 * "Completed"), then "Booking reference {ref}" in meta ("N/A" when missing). Edit mode only, for every
 * status (Flutter `appointment_status_widget.dart`). The old Cancelled / No Show rows are gone: the chip
 * itself says it. A Cancelled appointment still shows its reason and note under the line.
 *
 * FHIR mapping: Appointment.status (booked / fulfilled / cancelled / noshow).
 */

interface AppointmentStatusSummaryProps {
  currentStatus?: { code?: string | null; display?: string | null } | null;
  /** Backend booking reference (Flutter `appointment.referenceId`). */
  referenceId?: string | number | null;
  /** Cancel reason + note, shown under the line when the status is Cancelled. */
  cancelationReason?: { display?: string | null } | null;
  cancellationNote?: string | null;
}

export function AppointmentStatusSummary({
  currentStatus,
  referenceId,
  cancelationReason,
  cancellationNote,
}: AppointmentStatusSummaryProps) {
  const code = (currentStatus?.code ?? '').trim();
  const display = currentStatus?.display?.trim();
  const label = /^fulfilled$/i.test(code) ? 'Completed' : display || code || 'Booked';
  const ref =
    referenceId != null && `${referenceId}`.trim() !== '' ? referenceId : 'N/A';

  return (
    <section className="flex flex-col gap-3 px-6 py-4">
      <div className="flex items-center gap-2 whitespace-nowrap">
        <h3 className="text-xs font-medium text-text-secondary">Status</h3>
        <BridgeStatusChip semantic={appointmentStatusSemantic(code || label)} tone="soft">
          {label}
        </BridgeStatusChip>
        <span className="ml-2 min-w-0 truncate text-xs text-text-secondary">
          Booking reference {ref}
        </span>
      </div>
      <CancellationSummary
        status={currentStatus}
        reason={cancelationReason}
        note={cancellationNote}
        hideStatusLabel
      />
    </section>
  );
}
